import React, { useState } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '';
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(null); // null | 'verify' | 'setup'
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
  });

  const { login, register, verifyMfa, setupMfa, enableMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const postLoginPath = (userData) => {
    if (redirect) return `/${redirect}`;
    if (userData?.role === 'admin' || location.pathname === '/admin/login') return '/admin';
    return '/';
  };

  const finishAuth = (userData) => {
    toast.success(mfaStep === 'setup' ? 'MFA activé — connexion réussie' : 'Connexion réussie');
    navigate(postLoginPath(userData));
  };

  const resetMfa = () => {
    setMfaStep(null);
    setMfaToken('');
    setMfaCode('');
    setMfaSecret('');
    setProvisioningUri('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.mfa_required) {
          setMfaToken(result.mfa_token);
          setMfaStep('verify');
          toast.message('Saisissez le code de votre application d\'authentification');
          return;
        }
        if (result.mfa_setup_required) {
          setMfaToken(result.mfa_token);
          const setup = await setupMfa(result.mfa_token);
          setMfaSecret(setup.secret);
          setProvisioningUri(setup.provisioning_uri);
          setMfaStep('setup');
          toast.message('Configurez le MFA pour sécuriser votre espace créateur');
          return;
        }
        finishAuth(result.user);
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast.error('Les mots de passe ne correspondent pas');
          return;
        }
        await register(formData.email, formData.password, formData.firstName, formData.lastName);
        toast.success('Compte créé avec succès');
        navigate(redirect ? `/${redirect}` : '/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await verifyMfa(mfaToken, mfaCode);
      finishAuth(data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code MFA invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await enableMfa(mfaToken, mfaSecret, mfaCode);
      finishAuth(data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code MFA invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 md:p-12 border border-black/10"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex justify-center w-full">
              <BrandLogo size="md" tone="gold" />
            </Link>
            <h1 className="font-heading text-3xl mt-6 mb-2">
              {mfaStep === 'verify'
                ? 'Vérification MFA'
                : mfaStep === 'setup'
                  ? 'Configurer le MFA'
                  : isLogin
                    ? 'Connexion'
                    : 'Créer un compte'}
            </h1>
            <p className="text-foreground/60 text-sm">
              {mfaStep === 'verify'
                ? 'Entrez le code à 6 chiffres de votre application d\'authentification'
                : mfaStep === 'setup'
                  ? 'Scannez la clé dans Google Authenticator (ou équivalent), puis confirmez avec un code'
                  : isLogin
                    ? 'Connectez-vous pour accéder à votre compte'
                    : 'Rejoignez la communauté Maison Ka Couture'}
            </p>
          </div>

          {mfaStep === 'verify' && (
            <form onSubmit={handleMfaVerify} className="space-y-6" data-testid="mfa-verify-form">
              <div className="flex justify-center text-gold mb-2">
                <Shield size={32} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                  Code à 6 chiffres
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full bg-transparent border-b border-black/20 py-3 text-center text-2xl tracking-[0.5em] focus:border-gold focus:outline-none transition-colors"
                  data-testid="input-mfa-code"
                />
              </div>
              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="btn-primary w-full flex items-center justify-center"
                data-testid="mfa-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Valider'
                )}
              </button>
              <button
                type="button"
                onClick={resetMfa}
                className="w-full text-sm text-foreground/50 hover:text-gold transition-colors"
              >
                Retour à la connexion
              </button>
            </form>
          )}

          {mfaStep === 'setup' && (
            <form onSubmit={handleMfaEnable} className="space-y-6" data-testid="mfa-setup-form">
              <div className="p-4 border border-black/10 bg-black/5 text-sm space-y-2">
                <p className="text-foreground/50 text-xs uppercase tracking-widest">Clé secrète</p>
                <p className="font-mono text-gold break-all select-all" data-testid="mfa-setup-secret">
                  {mfaSecret}
                </p>
                {provisioningUri && (
                  <p className="text-foreground/40 text-xs break-all">{provisioningUri}</p>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                  Code de confirmation
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full bg-transparent border-b border-black/20 py-3 text-center text-2xl tracking-[0.5em] focus:border-gold focus:outline-none transition-colors"
                  data-testid="input-mfa-code"
                />
              </div>
              <button
                type="submit"
                disabled={loading || mfaCode.length !== 6}
                className="btn-primary w-full flex items-center justify-center"
                data-testid="mfa-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Activer le MFA et continuer'
                )}
              </button>
              <button
                type="button"
                onClick={resetMfa}
                className="w-full text-sm text-foreground/50 hover:text-gold transition-colors"
              >
                Retour à la connexion
              </button>
            </form>
          )}

          {!mfaStep && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                      Prénom
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required={!isLogin}
                      className="w-full bg-transparent border-b border-black/20 py-3 focus:border-gold focus:outline-none transition-colors"
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required={!isLogin}
                      className="w-full bg-transparent border-b border-black/20 py-3 focus:border-gold focus:outline-none transition-colors"
                      data-testid="input-last-name"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-white/20 py-3 focus:border-gold focus:outline-none transition-colors"
                  data-testid="input-email"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="w-full bg-transparent border-b border-black/20 py-3 pr-10 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-foreground/40 hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-xs uppercase tracking-widest text-foreground/60 mb-2">
                    Confirmer le mot de passe
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required={!isLogin}
                    minLength={6}
                    className="w-full bg-transparent border-b border-black/20 py-3 focus:border-gold focus:outline-none transition-colors"
                    data-testid="input-confirm-password"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  isLogin ? 'Se connecter' : 'Créer mon compte'
                )}
              </button>
            </form>
          )}

          {!mfaStep && (
            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ email: '', password: '', firstName: '', lastName: '', confirmPassword: '' });
                }}
                className="text-sm text-foreground/60 hover:text-gold transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin
                  ? "Pas encore de compte ? Créer un compte"
                  : "Déjà un compte ? Se connecter"}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
